import unset from "lodash/unset";
import isObject from "lodash/isObject";
import { PublicSettings } from "@/src/config/PublicSettings";
import { isHashLink, isRelativeLink } from "@/src/utils/validation";
import { NextRouter } from "next/router";
import { VideoDetails, VideoSchema } from "./@types/video";
import { IFaqFields } from "./@types/contentful";
import Logo from "@/src/media/logo.svg";
import { BrandService } from "./BrandService";
import { InsuranceAgency } from "schema-dts";
import { TrustPilotSummary } from "./@types/TrustPilot";

const LOCAL_URL_ROOTS = [
    // These URL roots will be considered local to this site.
    "/",
    "https://spotpetins.com",
    "https://spotpet.com",
    PublicSettings.CANONICAL_URL_ROOT
];

/** Matches the suffix Utils.withPageSuffix appends. */
const PAGE_SUFFIX_PATTERN = / - Page \d+$/;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * "Brand name at the very end, preceded by the start of the string, whitespace or a separator (|, -, –, —)",
 * case-insensitive. Brand names never change at runtime, so each pattern is built once rather than on every
 * SiteContainer render.
 */
const endsWithBrandPatterns = new Map<string, RegExp>();
const getEndsWithBrandPattern = (brandName: string): RegExp => {
    let pattern = endsWithBrandPatterns.get(brandName);
    if (!pattern) {
        pattern = new RegExp(`(?:^|[\\s|\\-–—])\\s*${escapeRegExp(brandName)}$`, "i");
        endsWithBrandPatterns.set(brandName, pattern);
    }
    return pattern;
};

/** One byline credit, as the blog and news routes build it, for Utils.generateArticleAuthorship. */
export interface ArticleBylineEntry {
    name: string;
    /** Author page slug; without one no URL is emitted. */
    slug?: string;
    /** From the post's `contributors` field (reviewers): in the byline, but not an author of the article. */
    isContributor?: boolean;
    /** The author entry's `isOrganization`: a team or clinic rather than a person. */
    isOrganization?: boolean;
}

export class Utils {
    static interleave = (arr: any[], thing: any) => {
        return ([] as any).concat(...arr.map(n => [n, thing])).slice(0, -1);
    };

    static normalizeURL(inputURL: string, makeAbsolute?: boolean): { normalizedURL: string; isExternal: boolean; isWebURL: boolean } {
        inputURL = inputURL.trim();

        const returnValue = {
            normalizedURL: inputURL,
            isExternal: true,
            isWebURL: true
        };

        if (inputURL.startsWith("tel:") || inputURL.startsWith("mailto:")) {
            returnValue.isWebURL = false;
        }

        if (isRelativeLink.test(inputURL) || isHashLink.test(inputURL)) {
            returnValue.isExternal = false;
        } else {
            for (const localURLRoot of LOCAL_URL_ROOTS) {
                // Is this a local URL?
                if (inputURL.startsWith(localURLRoot)) {
                    // This is a local URL
                    returnValue.isExternal = false;
                    // Modify the URL to be a relative local URL
                    returnValue.normalizedURL = inputURL.replace(localURLRoot, "");
                    break;
                }
            }
        }

        if (!returnValue.isExternal && makeAbsolute) {
            returnValue.normalizedURL = PublicSettings.CANONICAL_URL_ROOT + returnValue.normalizedURL;
        }

        if (returnValue.normalizedURL === "") {
            returnValue.normalizedURL = "/";
        }

        return returnValue;
    }

    static extractPathname(url: string): string {
        if (url.startsWith("tel:") || url.startsWith("mailto:")) {
            return url;
        }

        try {
            const u = url.startsWith("http://") || url.startsWith("https://") ? new URL(url) : new URL(url, "http://dummy");
            return u.pathname.toLowerCase();
        } catch {
            const path = url.split("?")[0].split("#")[0].toLowerCase();
            return path.startsWith("/") ? path : `/${path}`;
        }
    }

    static getQueryParameters(): Record<string, any> {
        try {
            return Object.fromEntries(new URLSearchParams(document.location.search).entries());
        } catch (e) {}

        return {};
    }

    static getCurrentPath(router: NextRouter): string {
        const { asPath } = router;
        const cleanPath = asPath.split("#")[0].split("?")[0];
        return asPath === "/" ? "" : cleanPath;
    }

    static getCurrentCanonicalURL(router: NextRouter, rootURL: string): string {
        return `${rootURL}` + Utils.getCurrentPath(router);
    }

    /**
     * Removes undefined values from an object. Useful for Next.js throwing errors like Reason: `undefined` cannot be serialized as JSON. Please use `null` or omit this value.
     * @param inputObject
     */
    static cleanUndefinedValues<T extends Record<string, any> = Record<string, any>>(inputObject: T): T {
        for (const key of Object.keys(inputObject)) {
            const value = inputObject[key];
            if (value === undefined) {
                unset(inputObject, key);
            } else if (isObject(value)) {
                (inputObject as any)[key] = Utils.cleanUndefinedValues(value);
            }
            // TODO: add support for arrays
        }
        return inputObject;
    }

    static getQueryParameter(name: string): string | undefined {
        return Utils.getQueryParameters()[name];
    }

    static getUserPreferences(): Record<string, any> {
        try {
            return JSON.parse(window.localStorage.getItem("spot-prefs") ?? "{}");
        } catch (e) {
            console.error("Unable to parse user prefs", e);
        }

        return {};
    }

    static saveUserPreference(name: string, value: any) {
        const userPrefs = Utils.getUserPreferences();
        userPrefs[name] = value;

        try {
            window.localStorage.setItem("spot-prefs", JSON.stringify(userPrefs));
        } catch (e) {
            console.error("Unable to save user prefs", e);
        }
    }

    static getUserPreference(name: string, defaultValue?: any): any | undefined {
        const userPrefs = Utils.getUserPreferences();
        return userPrefs[name] ?? defaultValue;
    }

    static extractFileExtension(location: string): string | undefined {
        function tryCreateURL(urlString: string) {
            try {
                return new URL(urlString);
            } catch (e) {
                // Ignore
            }
        }

        let url = tryCreateURL(location);

        // If this doesn't work, try adding a file:// at the beginning to make it proper URL
        if (!url) {
            url = tryCreateURL("file://" + location);
        }

        if (!url) {
            return;
        }

        const matches = url.pathname.match(/[.](.+?)$/);
        if (!matches || matches.length <= 1) {
            return;
        }

        return matches[1];
    }

    static getPlaceholderImageURL(imageURL: string): string {
        return `/_next/image?url=${encodeURIComponent(imageURL)}&q=1&w=128`;
    }

    /* converts a Contentful Document to plain HTML */
    static documentToHtml = (document: any[]) => {
        return document
            .map(node => {
                /* SIMPLE PARAGRAPHS */
                if (node.content.length === 1 && node.nodeType === "paragraph") {
                    return `<p>${node.content[0].value}</p>`;
                }
                /* PARAGRAPHS WITH LINKS */
                if (node.content.length > 1 && node.nodeType === "paragraph") {
                    return `<p>${node.content
                        .flatMap((inner: any) => {
                            switch (inner.nodeType) {
                                case "text":
                                    return inner.value;
                                case "hyperlink":
                                    return `<a href="${inner.data.uri}">${inner.content[0].value}</a>`;
                            }
                        })
                        .join("")}</p>`;
                }
                /* UNORDERED LISTS */
                if (node.nodeType === "unordered-list") {
                    return `<ul>${node.content
                        .flatMap((inner: any) => {
                            return `<li>${inner.content[0].content[0].value}</li>`;
                        })
                        .join("")}</ul>`;
                }
            })
            .join("");
    };

    /**
     * Appends " - Page {n}" on page 2+ of paginated list pages so every page in
     * the series gets a unique title/meta description (ENG-16924, Semrush QW-4).
     */
    static withPageSuffix = <T extends string | undefined>(text: T, pageNumber?: number): T => {
        return (text && pageNumber && pageNumber > 1 ? `${text} - Page ${pageNumber}` : text) as T;
    };

    /**
     * Appends " | <brand>" to a page title unless the brand name already ends the authored title, however it is
     * separated ("| Spot Pet Insurance", "- Spot Pet Insurance", "at Spot Pet Insurance", ...). The previous guard
     * only recognised its own exact " | <brand>" format, so an authored title that mentioned the brand as prose
     * ("Jane Doe | Author at Spot Pet Insurance") had it appended a second time (ENG-19518).
     *
     * Pagination: withPageSuffix appends " - Page N" after the authored title, so the brand check is made with that
     * suffix set aside and the suffix is kept in place:
     *   "Blog - Page 2"                                    → "Blog - Page 2 | Spot Pet Insurance" (as before)
     *   "Jane Doe | Author at Spot Pet Insurance - Page 2" → unchanged (brand once)
     *
     * The title is trimmed first, which also removes the stray double space a trailing space in Contentful used to
     * produce. Length is deliberately not touched here; that is the title-length policy ticket (ENG-19536).
     *
     * `otherBrandNames` also count as the brand already being there: Canada suffixes "Spot Pet Insurance" but some
     * authored titles end with its full name, "Spot Pet Insurance Canada", which should not get a second brand.
     */
    static withBrandSuffix = (rawTitle: string, brandName: string, otherBrandNames: (string | undefined)[] = []): string => {
        const title = rawTitle.trim();
        if (!title) {
            return brandName;
        }

        const pageSuffix = title.match(PAGE_SUFFIX_PATTERN)?.[0] ?? "";
        const baseTitle = pageSuffix ? title.slice(0, -pageSuffix.length).trimEnd() : title;

        if ([brandName, ...otherBrandNames].some(name => name && getEndsWithBrandPattern(name).test(baseTitle))) {
            return `${baseTitle}${pageSuffix}`;
        }

        return `${baseTitle}${pageSuffix} | ${brandName}`;
    };

    //Function to convert strings to camel case
    static camelize = (str: string) => {
        return str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
                return index === 0 ? word.toLowerCase() : word.toUpperCase();
            })
            .replace(/\s+/g, "");
    };

    // Copy text to clipboard
    static async clickToCopy(text: string, callback?: () => void) {
        try {
            await navigator.clipboard.writeText(text);
            const copiedText = await navigator.clipboard.readText();

            if (callback && text === copiedText) callback();
        } catch (error) {
            // Unable to copy text: permission denied, async issue etc.
        }
    }

    //Formats a date string into 'MM/DD/YY' format for display.
    static formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "2-digit" };
        return date.toLocaleDateString("en-US", options);
    };

    static formatPhone = (phone: string, delimiter: string = "-") => {
        let formattedPhone = "";
        const phoneLength = phone.length;

        switch (phoneLength) {
            case 11:
                // Handle phone number with country code (11 characters)
                formattedPhone = phone.slice(0, 1) + delimiter + phone.slice(1, 4) + delimiter + phone.slice(4, 7) + delimiter + phone.slice(7);
                break;
            case 10:
                formattedPhone = phone.slice(0, 3) + delimiter + phone.slice(3, 6) + delimiter + phone.slice(6);
                break;

            default:
                formattedPhone = phone;
                break;
        }
        return formattedPhone;
    };

    static parseCookies(cookieString: string | undefined): Record<string, string> {
        if (!cookieString) return {};

        return cookieString.split(";").reduce((cookies, cookie) => {
            const [key, value] = cookie.split("=").map(part => part.trim());
            if (key && value) {
                cookies[key] = decodeURIComponent(value);
            }
            return cookies;
        }, {} as Record<string, string>);
    }

    /* SCHEMA GENERATING FUNCTIONS */

    static generateVideoSchemas = (videos: VideoDetails[]): VideoSchema[] => {
        return videos.map(video => ({
            "@type": "VideoObject",
            name: video.name,
            description: video.description,
            thumbnailUrl: video.thumbnail,
            uploadDate: video.uploadDate,
            duration: video.duration,
            contentUrl: `https://www.youtube.com/watch?v=${video.id}`,
            embedUrl: `https://www.youtube.com/embed/${video.id}`
        }));
    };

    static generateWebsiteSchema = () => {
        const BRAND_DATA = BrandService.getCurrentBrandData();
        return {
            "@type": "WebSite",
            "@id": `${BRAND_DATA.productionUrl}/#website`,
            url: BRAND_DATA.productionUrl,
            name: BRAND_DATA.name,
            publisher: { "@id": `${BRAND_DATA.productionUrl}/#organization` },
            inLanguage: BRAND_DATA.i18n.defaultLocale
        };
    };

    /**
     * WebPage node for a page. `isPartOf` points at the WebSite node, so emit generateWebsiteSchema() on the same page
     * (PageStructuredData does). `about` is only set when the caller passes `aboutId`, i.e. the homepage's Service node;
     * other pages emit no `about` rather than a reference to a node that is not on the page.
     */
    static generateWebPageSchema = (config: { url: string; name: string; description?: string; aboutId?: string }) => {
        const BRAND_DATA = BrandService.getCurrentBrandData();

        return {
            "@type": "WebPage",
            "@id": `${config.url}/#webpage`,
            url: config.url,
            name: config.name,
            description: config.description,
            isPartOf: { "@id": `${BRAND_DATA.productionUrl}/#website` },
            ...(config.aboutId ? { about: { "@id": config.aboutId } } : {}),
            breadcrumb: { "@id": `${config.url}/#breadcrumb` },
            inLanguage: BRAND_DATA.i18n.defaultLocale
        };
    };

    /**
     * FAQPage node. Components pass `id` as `${pageUrl}/#faq` from usePageSchemaURL, so every page's FAQ node has its
     * own @id; two pages sharing one are treated as one thing by search engines. The default is the homepage's.
     */
    static generateFaqSchemaFromQA = (qa: { question: string; answerHtml: string }[], id?: string) => {
        const BRAND_DATA = BrandService.getCurrentBrandData();

        return {
            "@type": "FAQPage",
            "@id": id ?? `${BRAND_DATA.productionUrl}/#faq`,
            inLanguage: BRAND_DATA.i18n.defaultLocale,
            mainEntity: qa.map(({ question, answerHtml }) => ({
                "@type": "Question",
                name: question,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: answerHtml
                }
            }))
        };
    };

    static generateFaqPageSchema = (faqs: IFaqFields[], id?: string) =>
        Utils.generateFaqSchemaFromQA(
            faqs.map(faq => ({ question: faq.question, answerHtml: Utils.documentToHtml(faq.answer.content) })),
            id
        );

    static generateOrganizatonSchema = () => {
        const BRAND_DATA = BrandService.getCurrentBrandData();
        const logoURL = Utils.normalizeURL(Logo.src, true).normalizedURL;

        // InsuranceAgency is a schema.org subtype of FinancialService/LocalBusiness/Organization,
        // so it remains valid anywhere an Organization is expected (e.g. article author/publisher).
        const org: InsuranceAgency = {
            "@type": "InsuranceAgency",
            "@id": `${BRAND_DATA.productionUrl}/#organization`,
            name: BRAND_DATA.name,
            legalName: BRAND_DATA.country === "Canada" ? "Spot Pet Insurance Services ULC" : "Spot Pet Insurance Services",
            url: BRAND_DATA.productionUrl,
            logo: {
                "@type": "ImageObject",
                url: logoURL
            },
            description: BRAND_DATA.description,
            areaServed: BRAND_DATA.countryCode,
            contactPoint: [
                {
                    "@type": "ContactPoint",
                    telephone: PublicSettings.CS_PHONE_NUMBER,
                    contactType: "Customer Support",
                    areaServed: BRAND_DATA.countryCode,
                    availableLanguage: BRAND_DATA.i18n.defaultLocale
                }
            ],
            address: {
                "@type": "PostalAddress",
                streetAddress: BRAND_DATA.streetAddress,
                addressLocality: BRAND_DATA.addressLocality,
                addressRegion: BRAND_DATA.addressRegion,
                postalCode: BRAND_DATA.postalCode,
                addressCountry: BRAND_DATA.countryCode
            },
            sameAs: [
                BRAND_DATA.socials.instagram,
                BRAND_DATA.socials.facebook,
                BRAND_DATA.socials.linkedin,
                BRAND_DATA.socials.tiktok,
                BRAND_DATA.socials.twitter,
                BRAND_DATA.socials.youtube
            ]
        };

        // Awards and press coverage are US-specific (Forbes/MarketWatch rankings, spotpet.com Trustpilot page)
        if (BRAND_DATA.country === "United States") {
            org.award = ["Forbes 5 Star-Rated Best Choices of Annual Coverage", "MarketWatch Best for Most Customizable Coverage"];
            org.subjectOf = [
                {
                    "@type": "Article",
                    name: "Best Pet Insurance Companies Of 2026",
                    url: "https://www.forbes.com/financial-services/best-pet-insurance/",
                    publisher: {
                        "@type": "Organization",
                        name: "Forbes"
                    }
                },
                {
                    "@type": "WebPage",
                    name: "Rated Excellent",
                    url: "https://www.trustpilot.com/review/spotpet.com",
                    publisher: {
                        "@type": "Organization",
                        name: "Trustpilot"
                    }
                },
                {
                    "@type": "Article",
                    name: "Best for Most Customizable Coverage",
                    url: "https://www.marketwatch.com/insurance-services/pet-insurance/best-pet-insurance/",
                    publisher: {
                        "@type": "Organization",
                        name: "MarketWatch"
                    }
                }
            ];
        }

        // Add parent organization for non US brands
        if (BRAND_DATA.country !== "United States") {
            org.parentOrganization = {
                "@type": "Organization",
                "@id": "https://spotpet.com/#organization",
                name: "Spot Pet Insurance Services"
            };
        }

        return org;
    };

    static generateServiceSchema = (
        config: {
            serviceId?: string;
            /**
             * Trustpilot score to publish as the service's aggregateRating. Pass it only when the same figure is visible
             * on the page (the Trustpilot carousel): Google's structured-data policy requires marked-up ratings to be
             * shown to visitors, and markup for an invisible rating risks a manual action (ENG-19539).
             */
            trustPilotRating?: TrustPilotSummary;
        } = {}
    ) => {
        const BRAND_DATA = BrandService.getCurrentBrandData();
        const serviceId = config.serviceId || `${BRAND_DATA.productionUrl}/#service`;

        const service: Record<string, any> = {
            "@type": "Service",
            "@id": serviceId,
            name: "Pet Insurance",
            serviceType: "Dog & Cat Insurance",
            provider: { "@id": `${BRAND_DATA.productionUrl}/#organization` },
            areaServed: BRAND_DATA.countryCode,
            audience: {
                "@type": "PeopleAudience",
                name: `Pet owners in ${BRAND_DATA.country}`
            },
            potentialAction: {
                "@type": "QuoteAction",
                name: "Get a free quote",
                target: PublicSettings.QUOTE_FORM_URL_ROOT
            }
        };

        // Must match the carousel's rating line exactly (getRatingText in src/utils/trustPilotRating.ts).
        if (config.trustPilotRating) {
            service.aggregateRating = {
                "@type": "AggregateRating",
                ratingValue: config.trustPilotRating.score,
                bestRating: "5",
                ratingCount: config.trustPilotRating.totalReviews
            };
        }

        return service;
    };

    static generateBlogSchema = (
        config: {
            blogId?: string;
            name?: string;
            description?: string;
        } = {}
    ) => {
        const BRAND_DATA = BrandService.getCurrentBrandData();
        const blogUrl = `${BRAND_DATA.productionUrl}/blog`;

        return {
            "@type": "Blog",
            "@id": config.blogId || `${blogUrl}/#blog`,
            url: blogUrl,
            name: config.name || `${BRAND_DATA.name} Blog`,
            description: config.description || BRAND_DATA.description,
            publisher: { "@id": `${BRAND_DATA.productionUrl}/#organization` },
            inLanguage: BRAND_DATA.i18n.defaultLocale
        };
    };

    /**
     * `author` and `publisher` for an Article / BlogPosting / NewsArticle node (ENG-19519, ENG-19525).
     *
     * Only those credited as writers count: contributors (the post's `contributors` field, "Reviewed by" in the byline)
     * are not authors and are left out. A person becomes a Person node and an organisation author (the author entry's
     * `isOrganization`, e.g. "Spot Team" or a clinic) an Organization node, each linked to its author page when it has
     * a slug. Names are trimmed because a few Contentful entries carry trailing spaces. The organisation is the
     * publisher, referenced by @id, and with nobody credited it is the author as well, which is accurate for content
     * such as breed guides that has no byline. Either way the page must also emit generateOrganizatonSchema() so the
     * reference resolves on-page.
     */
    static generateArticleAuthorship = (byline: ArticleBylineEntry[]) => {
        const BRAND_DATA = BrandService.getCurrentBrandData();
        const organization = { "@id": `${BRAND_DATA.productionUrl}/#organization` };

        const authors = byline
            .filter(entry => !entry.isContributor)
            .map(entry => {
                const node = { name: entry.name.trim(), ...(entry.slug && { url: `${BRAND_DATA.productionUrl}/blog/authors/${entry.slug}` }) };
                return entry.isOrganization ? { "@type": "Organization" as const, ...node } : { "@type": "Person" as const, ...node };
            });

        return {
            author: authors.length > 0 ? authors : [organization],
            publisher: organization
        };
    };

    /**
     * The page-level node set every indexable template should carry: organisation (InsuranceAgency), WebSite, WebPage
     * and BreadcrumbList, with @id values scoped to `config.url`. Every @id these nodes reference is in the set, so
     * nothing dangles. Rendered by PageStructuredData (ENG-19529, ENG-19530).
     */
    static generatePageSchemas = (config: { url: string; name: string; description?: string; breadcrumbs: Array<{ name: string; url: string }> }) => [
        Utils.generateOrganizatonSchema(),
        Utils.generateWebsiteSchema(),
        Utils.generateWebPageSchema({ url: config.url, name: config.name, description: config.description }),
        Utils.generateBreadcrumbSchema({ id: `${config.url}/#breadcrumb`, items: config.breadcrumbs })
    ];

    static generateBreadcrumbSchema = (config: { id: string; items: Array<{ name: string; url: string }> }) => {
        return {
            "@type": "BreadcrumbList",
            "@id": config.id,
            itemListElement: config.items.map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.name,
                item: item.url
            }))
        };
    };
}
